import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { getEvent, updateEvent, deleteEvent, api, claimEvent, WS_BASE, patchScores } from "@/lib/api";
import { DEFAULT_COURSES } from "@/lib/constants";
import { loadOutbox, saveOutbox, mergeOps, applyOps, cacheEvent, loadCachedEvent } from "@/lib/outbox";
import { getMyProfile, getEventByCode } from "@/lib/api";
import { rememberTrip, tripCode } from "@/lib/access";
import { ChevronLeft, Home as HomeIcon, Settings, Flag, List, Trophy, Circle, Plus, Share2, Copy, MessageCircle, Users as UsersIcon, Lock, Radio, WifiOff, CloudUpload } from "lucide-react";
import { initials, withDisplayNames } from "@/lib/scoring";
import { useAuth } from "@/context/AuthContext";
import SetupTab from "@/screens/tabs/SetupTab";
import ScoreTab from "@/screens/tabs/ScoreTab";
import LiveTab from "@/screens/tabs/LiveTab";
import ChampionTab from "@/screens/tabs/ChampionTab";
import BbbTab from "@/screens/tabs/BbbTab";
import RoundSummaryModal from "@/screens/RoundSummaryModal";
import NewDayWizard from "@/screens/NewDayWizard";

function getDeviceId() {
  try {
    let id = localStorage.getItem("tga-device-id");
    if (!id) {
      id = Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
      localStorage.setItem("tga-device-id", id);
    }
    return id;
  } catch { return "anon"; }
}

function getScorerName() {
  try { return localStorage.getItem("tga-scorer-name") || ""; } catch { return ""; }
}

export default function EventShell({ eventId, onBack }) {
  const [event, setEvent] = useState(null);
  const [tab, setTab] = useState("score");
  const [dayId, setDayId] = useState(null);
  const [groupIdx, setGroupIdx] = useState(0);
  const [scorerId, setScorerId] = useState(() => { try { return localStorage.getItem(`tga-scorer-${eventId}`) || null; } catch { return null; } });
  const chooseScorer = (pid) => { setScorerId(pid); try { localStorage.setItem(`tga-scorer-${eventId}`, pid); } catch {} };
  const [holeIdx, setHoleIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [needsCode, setNeedsCode] = useState(false);
  const [codeInput, setCodeInput] = useState("");

  // A shared link may carry ?code=XXXXXX — keep it on this device, then tidy the URL
  if (typeof window !== "undefined") {
    const qp = new URLSearchParams(window.location.search).get("code");
    if (qp && !tripCode(eventId)) {
      rememberTrip({ id: eventId, code: qp });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (qp) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const savingRef = useRef(false);
  const localVersion = useRef(0);
  const pollRef = useRef(null);
  const deferredRef = useRef(null);
  const [liveConnected, setLiveConnected] = useState(false);
  const outboxRef = useRef(loadOutbox(eventId));
  const [pendingCount, setPendingCount] = useState(outboxRef.current.length);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const flushingRef = useRef(false);
  const flushTimer = useRef(null);
  const pendingFullSave = useRef(null);
  const deviceId = useMemo(() => getDeviceId(), []);
  const [scorerName, setScorerName] = useState(getScorerName());
  const { user } = useAuth();
  const canEdit = !!event && (!event.ownerUserId || (user && user.user_id === event.ownerUserId));
  const claimTriedRef = useRef(false);

  // Setup is organiser-only; a brand-new trip with no days opens on Setup for its organiser
  const firstOpenRef = useRef(true);
  useEffect(() => {
    if (!event) return;
    if (firstOpenRef.current) {
      firstOpenRef.current = false;
      if (canEdit && (event.days || []).length === 0) setTab("setup");
    } else if (tab === "setup" && !canEdit) setTab("score");
  }, [event, canEdit, tab]);

  // Signed-in users whose directory profile is a player in this trip score as themselves by default
  useEffect(() => {
    if (!user || !event || scorerId) return;
    getMyProfile().then((p) => {
      const me = p && (event.players || []).find((x) => x.profileId === p.id);
      if (me) chooseScorer(me.id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, event?.id, (event?.players || []).length]);

  // Auto-claim: first time a signed-in user opens an unowned trip, attach it
  useEffect(() => {
    if (!event || !user || claimTriedRef.current) return;
    if (!event.ownerUserId) {
      claimTriedRef.current = true;
      claimEvent(event.id).then((doc) => {
        setEvent((cur) => cur ? { ...cur, ownerUserId: doc.ownerUserId } : cur);
        toast.success(`${event.name} added to your trips`);
      }).catch((e) => { console.error("Claim event failed:", e); });
    }
  }, [event, user]);

  // Load event (falls back to the locally cached copy when offline)
  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await getEvent(eventId);
      if (!data.courses || Object.keys(data.courses).length === 0) {
        data.courses = JSON.parse(JSON.stringify(DEFAULT_COURSES));
      }
      cacheEvent(eventId, data);
      rememberTrip({ id: eventId, code: data.code, name: data.name });
      setNeedsCode(false);
      setEvent(applyOps(data, outboxRef.current));
      if (!dayId && data.days && data.days.length > 0) setDayId(data.days[0].id);
    } catch (e) {
      if (e?.response?.status === 403) {
        if (!silent) setNeedsCode(true);
        return;
      }
      if (e?.response?.status === 404) {
        if (!silent) { toast.error("Trip not found — it may have been deleted", { id: "trip-missing" }); onBack(); }
        return;
      }
      const cached = loadCachedEvent(eventId);
      if (cached && !silent) {
        setEvent(applyOps(cached, outboxRef.current));
        if (!dayId && cached.days?.length) setDayId(cached.days[0].id);
        setOnline(false);
        toast.info("You're offline — showing your saved copy. Scores will sync when back online.");
      } else if (!silent) toast.error("Could not load trip");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [eventId, dayId, onBack]);

  useEffect(() => { load(); }, [eventId]);

  // Apply a server doc only if newer than what we hold; defer while a local save is in flight.
  // Unsynced local score ops are always replayed on top so they never get wiped by a remote doc.
  const saveTimer = useRef(null);
  const applyRemote = useCallback((data) => {
    if (savingRef.current || saveTimer.current) { deferredRef.current = data; return; }
    if (!data.courses || Object.keys(data.courses).length === 0) {
      data.courses = JSON.parse(JSON.stringify(DEFAULT_COURSES));
    }
    cacheEvent(eventId, data);
    setEvent((cur) => {
      if (!cur) return applyOps(data, outboxRef.current);
      if (data.updatedAt && cur.updatedAt && data.updatedAt <= cur.updatedAt) return cur;
      return applyOps(data, outboxRef.current);
    });
  }, [eventId]);

  // ----- Offline-tolerant score outbox -----
  const flushOutbox = useCallback(async () => {
    if (flushingRef.current || outboxRef.current.length === 0) return;
    flushingRef.current = true;
    const batch = outboxRef.current.slice();
    try {
      const r = await patchScores(eventId, batch);
      outboxRef.current = outboxRef.current.filter((o) => !batch.includes(o));
      saveOutbox(eventId, outboxRef.current);
      setPendingCount(outboxRef.current.length);
      setOnline(true);
      if (r?.event) applyRemote(r.event);
      if (outboxRef.current.length > 0) setTimeout(flushOutbox, 50);
    } catch (e) {
      if (!e?.response) {
        setOnline(false);
      } else {
        console.error("score sync rejected", e);
        toast.error("Some scores could not be synced");
        outboxRef.current = [];
        saveOutbox(eventId, []);
        setPendingCount(0);
      }
    } finally {
      flushingRef.current = false;
    }
  }, [eventId, applyRemote]);

  const updateScores = useCallback((ops) => {
    setEvent((cur) => (cur ? applyOps(structuredClone(cur), ops) : cur));
    outboxRef.current = mergeOps(outboxRef.current, ops);
    saveOutbox(eventId, outboxRef.current);
    setPendingCount(outboxRef.current.length);
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flushOutbox, 300);
  }, [eventId, flushOutbox]);

  // Reconnect handling: browser online event + periodic retry while anything is pending
  useEffect(() => {
    const goOnline = () => { setOnline(true); flushOutbox(); if (pendingFullSave.current) { const p = pendingFullSave.current; pendingFullSave.current = null; doSave(p); } };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    const retry = setInterval(() => { if (outboxRef.current.length > 0) flushOutbox(); }, 10000);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); clearInterval(retry); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flushOutbox]);

  // Live sync over WebSocket with auto-reconnect; polling below is only the fallback
  useEffect(() => {
    let ws, retry, closed = false, attempt = 0;
    const connect = () => {
      const code = tripCode(eventId);
      try { ws = new WebSocket(`${WS_BASE}/ws/events/${eventId}${code ? `?code=${encodeURIComponent(code)}` : ""}`); } catch (e) { console.debug("ws create failed", e); return; }
      ws.onopen = () => { attempt = 0; setLiveConnected(true); setOnline(true); getEvent(eventId).then(applyRemote).catch(() => {}); flushOutbox(); };
      ws.onmessage = (m) => {
        if (m.data === "pong") return;
        try {
          const msg = JSON.parse(m.data);
          if (msg.type === "event") applyRemote(msg.event);
          else if (msg.type === "presence") setEvent((cur) => cur ? { ...cur, presence: msg.presence } : cur);
        } catch (e) { console.debug("ws message ignored", e); }
      };
      ws.onclose = (ev) => {
        setLiveConnected(false);
        if (closed) return;
        if (ev?.code === 4403) { closed = true; return; }
        attempt += 1;
        retry = setTimeout(connect, Math.min(15000, 1000 * 2 ** attempt));
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    connect();
    const ping = setInterval(() => { if (ws && ws.readyState === 1) ws.send("ping"); }, 25000);
    return () => { closed = true; clearInterval(ping); clearTimeout(retry); try { ws && ws.close(); } catch {} };
  }, [eventId, applyRemote, flushOutbox, event?.code]);

  // Fallback poll: 5s when the socket is down, 30s as a safety net when live
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      if (savingRef.current) return;
      try { applyRemote(await getEvent(eventId)); } catch (e) { console.debug("poll skipped:", e); }
    }, liveConnected ? 30000 : 5000);
    return () => clearInterval(pollRef.current);
  }, [eventId, liveConnected, applyRemote]);

  // Persist changes with debounce
  const scheduleSave = (next) => {
    setEvent(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => doSave(next), 400);
  };
  const doSave = async (payload) => {
    savingRef.current = true;
    saveTimer.current = null;
    try {
      const updated = await updateEvent(eventId, {
        name: payload.name,
        currency: payload.currency || "$",
        players: payload.players || [],
        courses: payload.courses || {},
        days: payload.days || [],
      });
      setEvent((cur) => ({ ...cur, updatedAt: updated.updatedAt }));
      pendingFullSave.current = null;
    } catch (e) {
      if (!e?.response) {
        pendingFullSave.current = payload;
        setOnline(false);
        toast.info("Offline — changes kept on this device and will sync when you reconnect");
      } else {
        toast.error("Save failed — will retry");
      }
    } finally {
      savingRef.current = false;
      if (deferredRef.current && !saveTimer.current) {
        const d = deferredRef.current; deferredRef.current = null; applyRemote(d);
      }
    }
  };

  const updateEventLocal = (patchFn) => {
    setEvent((cur) => {
      const next = patchFn(structuredClone(cur));
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => doSave(next), 400);
      return next;
    });
  };

  const currentDay = event?.days?.find((d) => d.id === dayId) || event?.days?.[0];
  const viewEvent = useMemo(() => withDisplayNames(event), [event]);
  useEffect(() => {
    if (!dayId && event?.days?.length) setDayId(event.days[0].id);
  }, [event, dayId]);

  // Presence heartbeat: only when Score tab is open, send groupIdx/holeIdx every ~5s
  useEffect(() => {
    if (tab !== "score" || !event) return;
    const send = () => {
      api.patch(`/events/${eventId}/presence`, {
        deviceId,
        name: scorerName || "Scorer",
        groupIdx,
        holeIdx,
      }).catch((e) => { console.debug("presence heartbeat failed:", e); });
    };
    send();
    const t = setInterval(send, 5000);
    return () => clearInterval(t);
  }, [tab, event, eventId, deviceId, scorerName, groupIdx, holeIdx]);

  const askScorerName = () => {
    const name = window.prompt("Your name (shows on Live so others know who's scoring):", scorerName || "");
    if (name === null) return;
    const t = name.trim();
    setScorerName(t);
    try { localStorage.setItem("tga-scorer-name", t); } catch (e) { console.warn("localStorage set failed:", e); }
  };

  const shareTrip = async () => {
    const url = `${window.location.origin}/trip/${event.id}?code=${event.code}`;
    const text = `You're invited to score ${event.name} on The TGA.\nTrip code: ${event.code}\nJoin here: ${url}\n\nOpen it on your phone, tap "Join a trip" and enter the code — you'll be able to enter scores for your group live.`;
    try {
      if (navigator.share) {
        await navigator.share({ title: event.name, text, url });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Trip link copied");
      }
    } catch (e) { console.error("shareTrip failed:", e); }
  };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(event.code); toast.success("Code copied"); } catch (e) { console.error("copyCode failed:", e); }
  };

  const onDeleteTrip = async () => {
    if (!window.confirm("Delete this trip and all scores? This cannot be undone.")) return;
    try {
      await deleteEvent(event.id);
      toast.success("Trip deleted");
      onBack();
    } catch {
      toast.error("Delete failed");
    }
  };

  const addDay = () => {
    if (!event) return;
    if (!event.players || event.players.length < 2) {
      toast.error("Add at least 2 players first");
      return;
    }
    setWizardOpen(true);
  };

  if (needsCode && !event) {
    const submitCode = async () => {
      const c = codeInput.trim().toUpperCase();
      if (c.length < 4) return;
      try {
        const ev = await getEventByCode(c);
        if (ev.id !== eventId) { toast.error("That code belongs to a different trip"); return; }
        rememberTrip({ id: ev.id, code: ev.code, name: ev.name });
        toast.success(`Joined "${ev.name}" — find it under Your trips`);
        onBack();
      } catch { toast.error("Trip code not found"); }
    };
    return (
      <div className="min-h-screen flex items-center justify-center px-5" data-testid="event-code-gate">
        <div className="tga-card w-full max-w-sm text-center">
          <img src="/logo-rounded.png" alt="TGA Golf" className="crest crest-lg crest-img mx-auto mb-4" />
          <div className="font-display text-2xl mb-1 inline-flex items-center gap-2"><Lock size={18} /> Private trip</div>
          <p className="helptext text-[color:var(--text-dark)]/70 mb-4">Enter the 6-character trip code from your organiser to open this trip on this phone.</p>
          <input
            data-testid="event-code-gate-input"
            className="tga-input-light w-full text-center font-mono-metrics tracking-[0.3em] uppercase text-lg"
            maxLength={6}
            placeholder="ABC123"
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === "Enter") submitCode(); }}
          />
          <div className="flex gap-2 mt-4">
            <button data-testid="event-code-gate-submit" className="brass-btn rounded-lg px-4 py-2 flex-1" onClick={submitCode}>Open trip</button>
            <button data-testid="event-code-gate-back" className="ghost-btn rounded-lg px-4 py-2" onClick={onBack}>Home</button>
          </div>
        </div>
      </div>
    );
  }

  if (loading || !event) {
    return <div className="min-h-screen flex items-center justify-center helptext">Loading trip…</div>;
  }

  const TAB_META = [
    ...(canEdit ? [{ id: "setup", label: "Setup", Icon: Settings }] : []),
    { id: "score", label: "Score", Icon: Flag },
    { id: "live", label: "Live", Icon: List },
    { id: "champ", label: "Champion", Icon: Trophy },
    { id: "bbb", label: "Side", Icon: Circle },
  ];

  return (
    <div className="min-h-screen pb-28 tga-fade">
      {/* Top bar */}
      <div className="sticky top-0 z-30 backdrop-blur bg-[color:var(--bg-deep)]/85 border-b border-[color:var(--border-brass-light)]">
        <div className="max-w-xl mx-auto px-4 py-3 flex items-center gap-3">
          <button data-testid="event-back-btn" onClick={onBack} className="ghost-btn px-2 py-2 rounded-lg" aria-label="Home">
            <HomeIcon size={16} />
          </button>
          <img src="/logo-rounded.png" alt="TGA Golf" className="crest crest-img" />
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl truncate text-[color:var(--brass-bright)]">{event.name}</div>
            <button onClick={copyCode} data-testid="event-copy-code" className="text-[11px] font-mono-metrics tracking-widest text-[color:var(--muted-green)] hover:text-[color:var(--brass)] inline-flex items-center gap-1">
              {event.code} <Copy size={11} />
              <span
                data-testid="event-live-indicator"
                title={!online ? "Offline — scores are saved on this device and will sync automatically" : liveConnected ? "Live sync connected" : "Reconnecting — syncing every 5s"}
                className={`ml-2 inline-flex items-center gap-1 normal-case tracking-normal ${!online ? "text-amber-400" : liveConnected ? "text-emerald-400" : "text-[color:var(--muted-green)]/60"}`}
              >
                {!online ? <WifiOff size={10} /> : <Radio size={10} className={liveConnected ? "animate-pulse" : ""} />}
                {!online ? "Offline" : liveConnected ? "Live" : "Sync"}
                {pendingCount > 0 && (
                  <span data-testid="event-pending-badge" className="ml-1 inline-flex items-center gap-0.5 text-amber-300">
                    <CloudUpload size={10} /> {pendingCount}
                  </span>
                )}
              </span>
            </button>
          </div>
          <button data-testid="event-name-btn" onClick={askScorerName} className="ghost-btn px-2 py-2 rounded-lg text-[10px] uppercase tracking-wide" title="Set your scorer name" aria-label="Set your name">
            {scorerName ? initials(scorerName) : <UsersIcon size={14} />}
          </button>
          <button data-testid="event-share-btn" onClick={shareTrip} className="ghost-btn px-2 py-2 rounded-lg" aria-label="Share">
            <Share2 size={16} />
          </button>
        </div>

        {/* Day chips */}
        <div className="max-w-xl mx-auto px-4 pb-3 overflow-x-auto">
          <div className="flex gap-2 items-center">
            {(event.days || []).map((d) => (
              <button
                key={d.id}
                data-testid={`event-day-chip-${d.label}`}
                className={`chip ${d.id === dayId ? "active" : ""}`}
                onClick={() => setDayId(d.id)}
              >
                {d.label}{d.finalized ? " ✓" : ""}
              </button>
            ))}
            {canEdit && (event.days?.length || 0) < 20 && (
              <button data-testid="event-add-day-btn" className="chip" onClick={addDay}>
                <Plus size={12} className="inline mr-1" /> Add day
              </button>
            )}
            {/* Presence pills — active scorers */}
            {(() => {
              const nowTs = Date.now() / 1000;
              const presence = event.presence || {};
              const active = Object.entries(presence)
                .filter(([id, v]) => id !== deviceId && (nowTs - (v.lastSeenTs || 0)) < 30)
                .slice(0, 6);
              if (active.length === 0) return null;
              return (
                <div className="flex items-center gap-1 ml-1" data-testid="event-presence-pills">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  {active.map(([id, v]) => (
                    <span
                      key={id}
                      className="avatar avatar-dark"
                      style={{ width: 22, height: 22, fontSize: 10 }}
                      title={`${v.name || "Scorer"} · Group ${typeof v.groupIdx === "number" ? String.fromCharCode(65 + v.groupIdx) : "?"} · Hole ${(v.holeIdx ?? 0) + 1}`}
                    >
                      {initials(v.name || "?") || "?"}
                    </span>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-xl mx-auto px-4 pt-4">
        {tab === "setup" && canEdit && (
          <SetupTab event={event} currentDay={currentDay} updateEventLocal={updateEventLocal} onDeleteTrip={onDeleteTrip} canEdit={canEdit} />
        )}
        {tab === "score" && (
          <ScoreTab
            event={viewEvent}
            canEdit={canEdit}
            currentDay={currentDay}
            groupIdx={groupIdx}
            setGroupIdx={setGroupIdx}
            holeIdx={holeIdx}
            setHoleIdx={setHoleIdx}
            updateEventLocal={updateEventLocal}
            updateScores={updateScores}
            scorerId={scorerId}
            setScorerId={chooseScorer}
            onOpenSummary={() => setSummaryOpen(true)}
          />
        )}
        {tab === "live" && (
          <LiveTab event={viewEvent} currentDay={currentDay} updateEventLocal={updateEventLocal} updateScores={updateScores} canEdit={canEdit} />
        )}
        {tab === "champ" && (
          <ChampionTab event={viewEvent} />
        )}
        {tab === "bbb" && (
          <BbbTab event={viewEvent} currentDay={currentDay} />
        )}
      </div>

      {/* Bottom tab bar */}
      <div className="tabbar">
        <div className="max-w-xl mx-auto flex items-stretch gap-1">
          {TAB_META.map(({ id, label, Icon }) => (
            <button
              key={id}
              data-testid={`event-tab-${id}`}
              className={`tab-btn ${tab === id ? "active" : ""}`}
              onClick={() => setTab(id)}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </div>
      </div>

      <RoundSummaryModal
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        onGoLive={() => { setSummaryOpen(false); setTab("live"); }}
        event={viewEvent}
        day={currentDay}
        groupIdx={groupIdx}
        scorerId={scorerId}
        updateScores={updateScores}
        canEdit={canEdit}
        onJumpHole={setHoleIdx}
      />

      <NewDayWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        event={viewEvent}
        updateEventLocal={updateEventLocal}
        onCreated={(dId) => { setDayId(dId); setTab("score"); }}
      />
    </div>
  );
}
